import React, {
  ComponentProps,
  FocusEventHandler,
  KeyboardEventHandler,
} from 'react';
import {
  CustomElement,
  FieldValues,
  UseFormReturn,
  useFormContext,
} from 'react-hook-form';

type IAutoCapitalize =
  | 'off'
  | 'none'
  | 'on'
  | 'sentences'
  | 'words'
  | 'characters';

type BaseInputProps = ComponentProps<'input'>;
export interface IInput
  extends Pick<
    BaseInputProps,
    | 'name'
    | 'type'
    | 'placeholder'
    | 'id'
    | 'value'
    | 'defaultValue'
    | 'onChange'
    | 'onBlur'
    | 'onFocus'
    | 'onKeyDown'
    | 'readOnly'
    | 'maxLength'
    | 'disabled'
    | 'autoComplete'
    | 'inputMode'
  > {
  ariaLabel?: string;
  autoCapitalize?: IAutoCapitalize;
  dataTestId?: string;
  hasError?: boolean;
  onClickIcon?: React.MouseEventHandler<HTMLInputElement> | undefined;
  register?:
    | ((
        ref: HTMLInputElement | HTMLTextAreaElement | CustomElement<any> | null,
      ) => void)
    | React.Ref<HTMLInputElement>;
  rounded?: boolean;
  size?: 'small' | 'medium' | 'large';
  withColoredBorder?: boolean;
  withClearIcon?: boolean;
}

const themeHeight = {
  small: '34px',
  medium: '42px',
  large: '55px',
};

const themePadding = {
  small: '6px 12px',
  medium: '0 28px 0 14px',
  large: '0 28px 0 14px',
};

const themeFontSize = {
  small: '14px',
  medium: '16px',
  large: '16px',
};

const Input: React.FC<IInput> = ({
  name,
  type,
  placeholder,
  register,
  size = 'medium' as const,
  rounded = false,
  id,
  value,
  defaultValue,
  onChange,
  onBlur,
  onClickIcon: onClick,
  onFocus,
  onKeyDown,
  readOnly,
  maxLength,
  ariaLabel,
  disabled = false,
  hasError = false,
  withColoredBorder = false,
  autoComplete,
  autoCapitalize,
  inputMode,
  dataTestId,
  withClearIcon = false,
}) => {
  const formContext = useFormContext() as UseFormReturn<FieldValues> | null;

  const baseOnBlur: FocusEventHandler<HTMLInputElement> = (e) => {
    if (name && type === 'email') {
      formContext?.setValue(name, e.target.value.trim());
    }

    onBlur?.(e);
  };

  const baseOnKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (name && type === 'email' && e.key === 'Enter') {
      formContext?.setValue(name, e.currentTarget.value.trim());
    }

    onKeyDown?.(e);
  };

  return (
    <div className="input-wrapper">
      <input
        id={id}
        name={name}
        type={type}
        placeholder={placeholder}
        ref={register}
        value={value}
        onChange={onChange}
        onBlur={baseOnBlur}
        onFocus={onFocus}
        onClick={onClick}
        onKeyDown={baseOnKeyDown}
        defaultValue={defaultValue}
        readOnly={readOnly}
        maxLength={maxLength}
        aria-label={ariaLabel}
        autoComplete={autoComplete}
        autoCapitalize={autoCapitalize}
        inputMode={inputMode}
        data-testid={dataTestId}
        disabled={disabled}
      />
      <style jsx>{`
        .input-wrapper {
          position: relative;
          width: 100%;
          height: ${size && themeHeight[size]};
        }

        input {
          font-family: var(--font-family-2);
          display: block;
          width: 100%;
          line-height: 1.5;
          color: var(--text-default-color);
          background-color: #fff;
          background-image: none;
          border: 1px solid;
          border-color: ${hasError
            ? 'var(--clr-danger)'
            : 'var(--clr-tertiary-2)'};
          border-radius: ${!rounded ? 'var(--rd-2)' : 'var(--rd-5)'};
          height: ${size && themeHeight[size]};
          padding: ${size && themePadding[size]};
          font-size: ${size && themeFontSize[size]};
          transition: border-color ease-in-out 0.15s,
            box-shadow ease-in-out 0.15s;
          outline: none;
          -webkit-appearance: none;
          padding-right: ${withClearIcon ? '75.5px' : '0'};
          position: absolute;
          top: 0;
          left: 0;
          ${withColoredBorder && !disabled && hasError
            ? 'border-color: var(--clr-danger);'
            : ''}
        }

        input:disabled {
          opacity: 0.3;
        }

        input:hover {
          ${withColoredBorder && !disabled
            ? `border-color: var(--clr-tertiary-5);`
            : ''};
        }

        input:focus {
          ${withColoredBorder && !disabled
            ? `
          border-color: var(--clr-tertiary-1);
          border-width: 1px;
        `
            : ''};
        }
      `}</style>
    </div>
  );
};

export default Input;
